// What a person types in Explore's field, as the search. Clauses the query
// language reads (group:Security -agent:Copilot) are the search as typed;
// other words go to a model with the fields and the values in reach, which
// answers with clauses. Either way the clauses are shown before they apply, so
// what narrows the traces is the query language's, deterministic and seen.
import { resolveField } from "./queryLang.js";
import { suggestValues } from "./queryLangSuggest.js";
import { sessionClauses } from "./sessionMatch.js";
import { runSubAgent } from "./subAgent.js";
import { appendClause } from "./workspaceSearch.js";

// The fields whose values are offered to the model, and how many of each.
const OFFERED = ["group", "identity", "agent", "model"];
const PER_FIELD = 30;
const FIELD_CLAUSE = /^-?([a-z_]+)(?::|!=|>=|<=|>|<)/i;

/** A search with `more` (one or several clauses) added, each once, before any aggregate it holds. */
export function addClauses(current, more) {
  return sessionClauses(more).clauses
    .map((c) => `${c.negated ? "-" : ""}${c.text}`)
    .reduce((acc, clause) => appendClause(acc, clause), String(current ?? ""));
}

/** Whether `text` is already clauses of the search: every part a known field and a value. */
export function isSearchSyntax(text) {
  const t = String(text || "").trim();
  if (!t) return false;
  const { clauses, errors } = sessionClauses(t);
  if (errors.length || !clauses.length) return false;
  return clauses.every((c) => {
    const m = FIELD_CLAUSE.exec(c.text);
    return !!(m && resolveField(m[1]));
  });
}

const SCHEMA = {
  type: "object",
  properties: {
    search: { type: "string", description: "The clauses, separated by spaces; empty when nothing the person typed can be a filter." },
    leftOver: { type: "string", description: "What the person typed that no clause expresses, in their words; empty when nothing is left." },
  },
  required: ["search"],
};

export const TRANSLATE_SYSTEM_PROMPT = `You translate what a person typed into Origin's Explore search box into the search's filter clauses. Explore lists AI agent traces; a trace is one conversation an agent ran for a person.

Fields:
- group: a team, as the directory names departments
- identity: a person, by name
- agent: the agent that ran the trace, by product (Claude, ChatGPT, Copilot, Gemini, Cursor, Local, ...)
- model: the model it used
- tool: a tool it called
- file: a file it touched
- text: words in its prompts, only when the person asks for words or a topic and no other field fits

Clause forms: field:value, field:"value with spaces", field:a,b for any of several values, and a leading "-" to filter a value out (-identity:"Cara Diaz"). Clauses are separated by spaces and all apply together.

Use the values listed in VALUES for the fields that have them, written exactly as listed: map what the person typed to the closest listed values (case and spelling may differ, a first name may name a listed person). When the words name a kind of agent or team, choose every listed value of that kind. Never invent a value for a listed field.

Times ("this week", "yesterday") are not clauses: the page's range applies. Costs and durations are not fields. Put anything you cannot express in leftOver. Return only clauses in search.`;

/** The values in reach for the fields offered to the model: field -> [value, ...]. */
export async function valuesInReach({ range, scopePath = [] } = {}) {
  const pairs = await Promise.all(OFFERED.map(async (field) => {
    const found = await suggestValues(field, "", { range, scopePath, limit: PER_FIELD });
    return [field, [...new Set(found.map((v) => v.value).filter(Boolean))]];
  }));
  return Object.fromEntries(pairs.filter(([, values]) => values.length));
}

/**
 * The search a sentence asks for: { search, leftOver } where `search` is
 * clauses the query language reads (maybe empty), or { error } where the
 * model could not be asked or answered with something the language rejects.
 */
export async function translateToSearch(sentence, { range, scopePath = [], signal, values = null } = {}) {
  const words = String(sentence || "").trim();
  if (!words) return { search: "", leftOver: "" };
  let offered = values;
  try {
    offered ??= await valuesInReach({ range, scopePath });
  } catch {
    offered = {};
  }
  let r;
  try {
    r = await runSubAgent({
      role: "task",
      signal,
      tools: [],
      outputSchema: SCHEMA,
      systemPrompt: TRANSLATE_SYSTEM_PROMPT,
      task: JSON.stringify({ typed: words, VALUES: offered }),
    });
  } catch (e) {
    return { error: e instanceof Error ? e : new Error(String(e)) };
  }
  if (r?.status !== "done" || !r.result) return { error: r?.error || new Error("the translation returned nothing") };
  const search = String(r.result.search || "").trim();
  const leftOver = String(r.result.leftOver || "").trim();
  if (search && !isSearchSyntax(search)) return { error: new Error(`not a search: ${search}`) };
  return { search: search.replace(/\s+/g, " "), leftOver };
}
